import { useCallback, useEffect, useState } from 'react';
import {
  DEMAND_METRICS,
  DEMAND_METRIC_LABELS,
  formatFormula,
  driverToDb,
  ActivityInUseError,
  type BoardActivityUpsert,
  type ActivityQualification,
  type BoardActivity,
  type Employee,
  type MetricDef,
  type MetricUnit,
  type Team,
} from '@lemontaigu/staff';
import { useActivityCatalog, useBoard, useMetricRegistry, useRoster } from '@lemontaigu/staff/react';
import {
  activityDraftError,
  activityToDraft,
  driverLabel,
  emptyActivityDraft,
  teamLabel,
  timeInputValue,
  DRIVERS,
  demandFormulaOf,
  TEAMS,
  type ActivityDraft,
} from './logic.js';

// « Activités » (operator-only, its own page since the bos-superset pass
// 2026-07-17): the activity × qualification matrix (parent brief §5.4) —
// default daily windows, demand drivers, teams, qualified sets. Direct RLS
// writes under the bos@ session (board_activities +
// board_activity_qualifications are is_admin-gated, migration 0032). The
// operational board (Jour/Semaine/Mois, demand, overrides, broadcast) is
// the Planning tab — the reception module mounted here.


export function Activites() {
  const board = useBoard();
  const roster = useRoster();
  const activityCatalog = useActivityCatalog();
  const metricRegistry = useMetricRegistry();
  const [activities, setActivities] = useState<BoardActivity[] | null>(null);
  const [qualifications, setQualifications] = useState<ActivityQualification[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [metrics, setMetrics] = useState<MetricDef[]>([]);
  const [err, setErr] = useState('');
  const [editingId, setEditingId] = useState<number | 'new' | null>(null);
  const [draft, setDraft] = useState<ActivityDraft | null>(null);
  const [draftErr, setDraftErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [sortBy, setSortBy] = useState<'team' | 'start'>('team');

  const load = useCallback(async () => {
    setErr('');
    try {
      const [acts, quals, emps, mets] = await Promise.all([
        board.activities(),
        board.qualifications(),
        roster.current(),
        metricRegistry.load(),
      ]);
      setActivities(acts);
      setQualifications(quals);
      setEmployees(emps);
      setMetrics(mets.filter((m) => m.active));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [board, roster, metricRegistry]);

  useEffect(() => {
    void load();
  }, [load]);

  const startEdit = (a: BoardActivity | null, team: '' | Team = '') => {
    setDraftErr('');
    if (a) {
      setEditingId(a.id);
      setDraft(activityToDraft(a));
    } else {
      const nextSort = Math.max(0, ...(activities ?? []).map((x) => x.sort)) + 10;
      setEditingId('new');
      setDraft({ ...emptyActivityDraft(nextSort), team });
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(null);
    setDraftErr('');
  };

  const saveDraft = async () => {
    if (!draft || editingId === null) return;
    const invalid = activityDraftError(draft);
    if (invalid) {
      setDraftErr(invalid);
      return;
    }
    setBusy(true);
    setDraftErr('');
    // The DOMAIN write shape since step 7 — the column names moved into
    // SupabaseActivityCatalogStore. What stays here is parsing the form.
    const payload: BoardActivityUpsert = {
      label: draft.label.trim(),
      // Untimed activities carry an unused 00:00 placeholder (columns stay NOT
      // NULL; the relaxed CHECK accepts it, no consumer reads it).
      windowStart: draft.untimed ? '00:00' : draft.windowStart,
      windowEnd: draft.untimed ? '00:00' : draft.windowEnd,
      untimed: draft.untimed,
      windowNote: draft.windowNote.trim() === '' ? null : draft.windowNote.trim(),
      demandDriver: driverToDb(draft.demandDriver),
      // Formula and threshold are meaningless for the 'always' anchors.
      demandFormula: draft.demandDriver === 'always' ? null : (demandFormulaOf(draft) ?? null),
      minDemand:
        draft.demandDriver === 'always' || draft.minDemand.trim() === ''
          ? null
          : Number(draft.minDemand.trim()),
      active: draft.active,
      sort: draft.sort.trim() === '' ? 0 : Number(draft.sort.trim()),
      team: draft.team === '' ? null : draft.team,
      // An 'always' anchor has no load to measure and nothing to badge.
      loadMetricKey: draft.demandDriver === 'always' || draft.loadMetricKey === '' ? null : draft.loadMetricKey,
      badgeMetricKey: draft.demandDriver === 'always' || draft.badgeMetricKey === '' ? null : draft.badgeMetricKey,
    };
    try {
      await activityCatalog.saveActivity(editingId === 'new' ? null : editingId, payload);
      cancelEdit();
      void load();
    } catch (e) {
      setDraftErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const removeActivity = async (a: BoardActivity) => {
    if (
      !window.confirm(
        `Supprimer « ${a.label} » ? Possible uniquement si elle n’a jamais servi (aucune présence enregistrée). Pour retirer une activité utilisée, décochez plutôt « Active » — son historique est préservé.`,
      )
    )
      return;
    setBusy(true);
    try {
      await activityCatalog.deleteActivity(a.id, a.label);
      void load();
    } catch (e) {
      // The FK-RESTRICT case arrives as a DOMAIN error now — the view states the
      // business rule in French without knowing PostgREST's 23503.
      if (e instanceof ActivityInUseError)
        setErr(`« ${a.label} » a un historique de présence — impossible de la supprimer. Décochez « Active » pour la retirer sans effacer l’historique.`);
      else setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleQualification = async (activityId: number, employeeId: string, qualified: boolean) => {
    setBusy(true);
    setErr('');
    try {
      // `qualified` is the CURRENT state, so the intent is its negation. The
      // service takes the intent, which is why the argument is inverted here
      // rather than the statement being chosen at the call site.
      await activityCatalog.setQualified(activityId, employeeId, !qualified);
      void load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const qualifiedSet = (activityId: number) =>
    new Set(qualifications.filter((q) => q.activityId === activityId).map((q) => q.employeeId));

  return (
    <div className="planning">
      <div className="equipe-head">
        <h2 className="section-title">Activités &amp; qualifications</h2>
        <span className="equipe-hint">
          Fenêtres par défaut — les ajustements au jour le jour (horaires, dispenses) se font sur l’écran
          Planning de la réception
        </span>
        <div className="activites-sort">
          <span>Trier :</span>
          <button
            type="button"
            className={sortBy === 'team' ? 'is-active' : ''}
            onClick={() => setSortBy('team')}
          >
            Par équipe
          </button>
          <button
            type="button"
            className={sortBy === 'start' ? 'is-active' : ''}
            onClick={() => setSortBy('start')}
          >
            Heure de début
          </button>
        </div>
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}
      {activities === null && <p className="staff-empty">Chargement…</p>}

      {activities !== null && sortBy === 'team' &&
        // Each team maintains ITS activity list in its own block (operator
        // request 2026-07-17); « + Ajouter » prefills the team, the form's
        // team select moves an activity between blocks. « Autres » (no team)
        // comes FIRST — those activities aren't covered by a team roster, so
        // they need the most visibility (operator request 2026-07-19).
        TEAM_SECTIONS.map((team) => {
          const group = activities
            .filter((a) => (a.team ?? '') === team)
            .sort((a, b) => a.windowStart.localeCompare(b.windowStart) || a.label.localeCompare(b.label, 'fr'));
          const addingHere = editingId === 'new' && draft && draft.team === team;
          if (team === '' && group.length === 0 && !addingHere) return null;
          return (
            <section key={team || 'autres'} className={`planning-team team-${team || 'none'}`}>
              <header className="planning-team-head">
                <span className="team-dot" />
                <h3>{team ? teamLabel(team) : 'Autres'}</h3>
                <span className="team-count">{group.length}</span>
              </header>
              <div className="planning-list">
                {group.map((a) => renderCard(a, false))}

                {addingHere && draft ? (
                  <section className="planning-card">
                    <header className="planning-card-head">
                      <strong>Nouvelle activité — {team ? teamLabel(team) : 'Autres'}</strong>
                    </header>
                    <ActivityForm
                      draft={draft}
                      error={draftErr}
                      busy={busy}
                      metrics={metrics}
                      onChange={setDraft}
                      onSave={() => void saveDraft()}
                      onCancel={cancelEdit}
                    />
                  </section>
                ) : (
                  <button type="button" className="planning-add" disabled={busy} onClick={() => startEdit(null, team)}>
                    + Ajouter une activité
                  </button>
                )}
              </div>
            </section>
          );
        })}

      {activities !== null && sortBy === 'start' && (
        // Flat cross-team list, purely chronological — each card colour-coded
        // by team (border + dot + label) since there's no section grouping
        // to convey it (operator request 2026-07-19).
        <div className="planning-list planning-list-flat">
          {activities
            .slice()
            .sort((a, b) => a.windowStart.localeCompare(b.windowStart) || a.label.localeCompare(b.label, 'fr'))
            .map((a) => renderCard(a, true))}

          {editingId === 'new' && draft ? (
            <section className="planning-card">
              <header className="planning-card-head">
                <strong>Nouvelle activité</strong>
              </header>
              <ActivityForm
                draft={draft}
                error={draftErr}
                busy={busy}
                metrics={metrics}
                onChange={setDraft}
                onSave={() => void saveDraft()}
                onCancel={cancelEdit}
              />
            </section>
          ) : (
            <button type="button" className="planning-add" disabled={busy} onClick={() => startEdit(null, '')}>
              + Ajouter une activité
            </button>
          )}
        </div>
      )}
    </div>
  );

  function renderCard(a: BoardActivity, showTeamTag: boolean) {
    const qualified = qualifiedSet(a.id);
    const editing = editingId === a.id && draft;
    return (
      <section
        key={a.id}
        className={`planning-card${showTeamTag ? ` team-${a.team || 'none'}` : ''}${a.active ? '' : ' is-inactive'}`}
      >
        <header className="planning-card-head">
          <div>
            {showTeamTag && <span className="team-dot" />}
            <strong>{a.label}</strong>
            {showTeamTag && <span className="planning-team-tag">{a.team ? teamLabel(a.team) : 'Autres'}</span>}
            {!a.active && <span className="emp-badge">inactive</span>}
            <span className="planning-window">
              {a.untimed
                ? 'Sans horaire · inscription'
                : `${timeInputValue(a.windowStart)}–${timeInputValue(a.windowEnd)} · ${driverLabel(a.demandDriver)}${
                    a.demandDriver !== 'always' && a.minDemand !== null ? ` · dès ${a.minDemand}` : ''
                  }`}
            </span>
            {a.windowNote && <span className="planning-note">{a.windowNote}</span>}
          </div>
          <div className="planning-actions">
            {!editing && (
              <button type="button" disabled={busy} onClick={() => startEdit(a)}>
                Modifier
              </button>
            )}
            <button type="button" className="ts-danger" disabled={busy} onClick={() => void removeActivity(a)}>
              Supprimer
            </button>
          </div>
        </header>

        {editing && (
          <ActivityForm
            draft={draft}
            error={draftErr}
            busy={busy}
            metrics={metrics}
            onChange={setDraft}
            onSave={() => void saveDraft()}
            onCancel={cancelEdit}
          />
        )}

        <div className="planning-qualified">
          <span className="planning-qualified-label">Qualifié·e·s :</span>
          {employees.map((e) => (
            <label key={e.id} className="planning-check">
              <input
                type="checkbox"
                checked={qualified.has(e.id)}
                disabled={busy}
                onChange={() => void toggleQualification(a.id, e.id, qualified.has(e.id))}
              />
              {e.firstName}
            </label>
          ))}
        </div>
      </section>
    );
  }
}

/** Section order mirrors the day Gantt's team grouping; '' = « Autres » (first — operator request 2026-07-19). */
const TEAM_SECTIONS: ('' | Team)[] = ['', ...TEAMS];

/** FR unit names for the picker hints — the whole point is that a load has one. */
const UNIT_LABEL: Record<MetricUnit, string> = {
  count: 'nombre',
  equivalents: 'à blancs équivalents',
  personMinutes: 'minutes de travail',
  heads: 'personnes',
};

function ActivityForm({
  draft,
  error,
  busy,
  metrics,
  onChange,
  onSave,
  onCancel,
}: {
  draft: ActivityDraft;
  error: string;
  busy: boolean;
  /** The registry (ADR-0015) — empty until it loads, which simply shows no options. */
  metrics: MetricDef[];
  onChange: (d: ActivityDraft) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const set = (patch: Partial<ActivityDraft>) => onChange({ ...draft, ...patch });
  const loadMetric = metrics.find((m) => m.key === draft.loadMetricKey) ?? null;
  return (
    <div className="planning-form">
      <label>
        Libellé
        <input type="text" value={draft.label} onChange={(e) => set({ label: e.target.value })} />
      </label>
      <label className="planning-check planning-form-wide">
        <input type="checkbox" checked={draft.untimed} onChange={(e) => set({ untimed: e.target.checked })} />
        Sans horaire (inscription — ex. « Téléphone ») : pas de plage, affichée après le Gantt
      </label>
      {!draft.untimed && (
        <>
          <label>
            Début
            <input type="time" value={draft.windowStart} onChange={(e) => set({ windowStart: e.target.value })} />
          </label>
          <label>
            Fin
            <input type="time" value={draft.windowEnd} onChange={(e) => set({ windowEnd: e.target.value })} />
          </label>
        </>
      )}
      <label>
        Demande
        <select
          value={draft.demandDriver}
          onChange={(e) => set({ demandDriver: e.target.value as ActivityDraft['demandDriver'] })}
        >
          {DRIVERS.map((d) => (
            <option key={d.value} value={d.value}>
              {d.label}
            </option>
          ))}
        </select>
      </label>
      {draft.demandDriver !== 'always' && (
        <>
          {/* ADR-0015: the activity no longer carries its own weighted sum — it
              POINTS AT a metric. The formula itself is edited once, in
              Métriques, and referenced from everywhere it is needed.

              Their own full-width row: a metric's label is a sentence (« À blanc
              équivalent — Ménage chambres »), so a <select> holding one has a
              large intrinsic width and blew out the 9rem auto-fit tracks the
              rest of the form uses, overlapping its neighbours. */}
          <div className="planning-form-wide planning-metrics">
          <label>
            Charge (métrique)
            <select value={draft.loadMetricKey} onChange={(e) => set({ loadMetricKey: e.target.value })}>
              <option value="">— formule héritée de l’activité —</option>
              {metrics.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.labelFr}
                </option>
              ))}
            </select>
            <span className="planning-hint">
              Ce que le seuil compare. Définie dans l’onglet Métriques.
              {loadMetric && ` Unité : ${UNIT_LABEL[loadMetric.unit]}.`}
            </span>
          </label>
          <label>
            Badge affiché sur le planning
            <select value={draft.badgeMetricKey} onChange={(e) => set({ badgeMetricKey: e.target.value })}>
              <option value="">— aucun —</option>
              {metrics.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.labelFr}
                </option>
              ))}
            </select>
            <span className="planning-hint">
              Le chiffre visible sur la barre du Gantt, avec son unité — ex. « à blanc équivalent » pour le
              ménage. Peut différer de la charge.
            </span>
          </label>
          </div>
          {draft.loadMetricKey === '' && (
            <div className="planning-form-wide planning-formula">
              <span className="planning-formula-title">Formule héritée (avant les métriques)</span>
              <span className="planning-hint">
                Cette activité utilise encore son ancienne formule interne. Choisissez une métrique ci-dessus
                pour n’avoir qu’une seule définition de la charge.
              </span>
              <span className="planning-formula-preview">{formatFormula(demandFormulaOf(draft))}</span>
            </div>
          )}
          <label>
            Seuil (à partir de)
            <input
              type="text"
              inputMode="numeric"
              placeholder="dès 1"
              value={draft.minDemand}
              onChange={(e) => set({ minDemand: e.target.value })}
            />
            <span className="planning-hint">
              En dessous, l’activité n’est pas requise (ex. Plonge : 20 couverts).
              {loadMetric && loadMetric.unit !== 'count' && (
                <>
                  {' '}
                  <strong>Attention :</strong> le seuil est exprimé en {UNIT_LABEL[loadMetric.unit]} —
                  ré-évaluez-le si vous venez de changer de métrique.
                </>
              )}
            </span>
          </label>
        </>
      )}
      <label>
        Équipe (groupe du tableau)
        <select value={draft.team} onChange={(e) => set({ team: e.target.value as ActivityDraft['team'] })}>
          <option value="">Autres</option>
          {TEAMS.map((t) => (
            <option key={t} value={t}>
              {teamLabel(t)}
            </option>
          ))}
        </select>
      </label>
      <label>
        Ordre
        <input type="text" inputMode="numeric" value={draft.sort} onChange={(e) => set({ sort: e.target.value })} />
      </label>
      <label className="planning-form-wide">
        Remarque (affichée sur le tableau)
        <input type="text" value={draft.windowNote} onChange={(e) => set({ windowNote: e.target.value })} />
      </label>
      <label className="planning-check">
        <input type="checkbox" checked={draft.active} onChange={(e) => set({ active: e.target.checked })} />
        Active (participe au calcul des postes à pourvoir)
      </label>
      {error && <p className="auth-err">{error}</p>}
      <div className="planning-form-actions">
        <button type="button" disabled={busy} onClick={onSave}>
          Enregistrer
        </button>
        <button type="button" disabled={busy} onClick={onCancel}>
          Annuler
        </button>
      </div>
    </div>
  );
}

